import { Box, HStack, Skeleton, useTheme, VStack } from "native-base";
import React from "react";
import { ScrollView } from "react-native";

import { Scale } from "../constants";
import RecipeCardSkeleton from "./RecipeCardSkeleton";

const SimpleRecipeInspirationSkeleton = (): JSX.Element => {
  const theme = useTheme();

  // Carousel skeleton component - single centered card
  const CarouselSkeleton = (): JSX.Element => (
    <Box mb={6}>
      {/* Section Title */}
      <Box mb={-2} alignItems="center">
        <Skeleton.Text lines={1} width="200px" />
      </Box>

      {/* Centered Card Container */}
      <Box height="300px" justifyContent="center" alignItems="center" mt={4}>
        <Box width="280">
          <RecipeCardSkeleton />
        </Box>
      </Box>

      {/* Carousel dots indicator - enhanced */}
      <HStack justifyContent="center" mt={8} space={2}>
        {[...Array(10)].map((_, index) => (
          <Box
            key={`dot-${index}`}
            width={index === 0 ? 3 : 2.5}
            height={index === 0 ? 3 : 2.5}
            borderRadius="full"
            bg={index === 0 ? "primary.40" : "gray.300"}
            opacity={index === 0 ? 1 : 0.5}
          />
        ))}
      </HStack>
    </Box>
  );

  // Categories section skeleton - horizontal scroll
  const CategoriesSkeleton = (): JSX.Element => (
    <Box mb={6}>
      <Box px={4} mb={2}>
        <Skeleton.Text lines={1} width="150px" />
      </Box>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: Scale(16), paddingBottom: 4 }}
      >
        {[1, 2, 3, 4].map((index) => (
          <Box
            key={`category-skeleton-${index}`}
            width="120"
            height="54"
            bg="primary.10"
            borderRadius={theme.borderRadii.card.default}
            mr={3}
            overflow="hidden"
            shadow="3"
          >
            <Skeleton height="100%" width="100%" />
          </Box>
        ))}
      </ScrollView>
    </Box>
  );

  // Search bar skeleton
  const SearchBarSkeleton = (): JSX.Element => (
    <Box px={4} mb={8} mt={4}>
      <Box
        bg="gray.50"
        borderRadius={theme.borderRadii.input.default}
        height={12}
        borderWidth={1}
        borderColor="gray.200"
        overflow="hidden"
        px={3}
      >
        <HStack alignItems="center" height="100%" space={2}>
          <Skeleton borderRadius="full" height={5} width={5} />
          <Skeleton.Text lines={1} flex={1} />
        </HStack>
      </Box>
    </Box>
  );

  // Recipe section skeleton - horizontal scroll like actual
  const RecipeSectionSkeleton = (): JSX.Element => (
    <Box mb={6}>
      <Box px={4}>
        <HStack justifyContent="space-between" alignItems="center" mb={2}>
          <Skeleton.Text lines={1} width="120px" />
          <Skeleton.Text lines={1} width="60px" />
        </HStack>
      </Box>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: Scale(16) }}
      >
        {[1, 2, 3].map((index) => (
          <Box key={`recipe-skeleton-${index}`} width={160} mr={3}>
            <RecipeCardSkeleton />
          </Box>
        ))}
      </ScrollView>
    </Box>
  );

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ paddingTop: 120, paddingBottom: 16 }}
      style={{ flex: 1, backgroundColor: theme.colors.primary[10] }}
    >
      {/* Carousel Skeleton */}
      <CarouselSkeleton />

      {/* Categories Section Skeleton */}
      <CategoriesSkeleton />

      {/* Search Bar Skeleton */}
      <SearchBarSkeleton />

      {/* Recipe Sections Skeleton */}
      <RecipeSectionSkeleton />
      <RecipeSectionSkeleton />
    </ScrollView>
  );
};

export default SimpleRecipeInspirationSkeleton;
